import { oc } from "@orpc/contract";
import { z } from "zod";
import { ResourceOriginSchema } from "./voice-agent-configs.contract";

export const PhoneNumberProvider = {
  Vapi: "vapi",
  Twilio: "twilio",
} as const;

export const PhoneNumberProviderSchema = z.enum([
  PhoneNumberProvider.Vapi,
  PhoneNumberProvider.Twilio,
]);

export type PhoneNumberProviderValue = z.infer<
  typeof PhoneNumberProviderSchema
>;

export const VoicePhoneNumberSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  provider: PhoneNumberProviderSchema,
  providerId: z.string().min(1),
  number: z.string().nullable(),
  name: z.string().nullable(),
  origin: ResourceOriginSchema,
  status: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type VoicePhoneNumberDTO = z.infer<typeof VoicePhoneNumberSchema>;

export const CreateVoicePhoneNumberInputSchema = z.object({
  provider: PhoneNumberProviderSchema,
  providerId: z.string().min(1),
  origin: ResourceOriginSchema,
  number: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
});

export type CreateVoicePhoneNumberInput = z.infer<
  typeof CreateVoicePhoneNumberInputSchema
>;

export const UpdateVoicePhoneNumberInputSchema = z.object({
  id: z.uuid(),
  name: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
});

export type UpdateVoicePhoneNumberInput = z.infer<
  typeof UpdateVoicePhoneNumberInputSchema
>;

const listVoicePhoneNumbers = oc
  .output(z.array(VoicePhoneNumberSchema))
  .route({ method: "GET" });

const getVoicePhoneNumber = oc
  .input(z.object({ id: z.uuid() }))
  .output(VoicePhoneNumberSchema)
  .route({ method: "GET" });

const createVoicePhoneNumber = oc
  .input(CreateVoicePhoneNumberInputSchema)
  .output(VoicePhoneNumberSchema);

const updateVoicePhoneNumber = oc
  .input(UpdateVoicePhoneNumberInputSchema)
  .output(VoicePhoneNumberSchema);

const deleteVoicePhoneNumber = oc
  .input(z.object({ id: z.uuid() }))
  .output(z.object({ success: z.boolean() }));

const releaseVoicePhoneNumber = oc
  .input(z.object({ id: z.uuid() }))
  .output(
    z.object({ success: z.boolean(), releasedFromProvider: z.boolean() }),
  );

export const voicePhoneNumberContracts = {
  list: listVoicePhoneNumbers,
  get: getVoicePhoneNumber,
  create: createVoicePhoneNumber,
  update: updateVoicePhoneNumber,
  delete: deleteVoicePhoneNumber,
  release: releaseVoicePhoneNumber,
} as const;

export type VoicePhoneNumberContracts = typeof voicePhoneNumberContracts;
